/**
 * iPhone cameras save photos as HEIC by default — neither the Seedream model
 * nor our own validateFile() regex (image/jpeg|png|webp) accept that, so an
 * unconverted upload either gets rejected client-side (UploadDropzone) or
 * silently stored as a mislabeled .jpg that breaks downstream
 * (MultiUpload/CanvasUpsellApp, which only check `type.startsWith('image/')`).
 * Convert to JPEG in-browser before any of those checks run.
 *
 * heic2any ships a WASM-ish decoder (~1.3MB) that can't be code-split out of
 * this widget's bundle — it's built as a single IIFE (vite.config.ts) for
 * Shopify asset embedding, and Rollup inlines every dynamic import in that
 * format. Loading it as a plain <script> from jsDelivr instead keeps it
 * fully lazy: fetched only the first time a HEIC file is actually picked,
 * same pattern theme.liquid already uses for Google Fonts.
 */

declare global {
  interface Window {
    heic2any?: (options: { blob: Blob; toType?: string; quality?: number }) => Promise<Blob | Blob[]>;
  }
}

const HEIC2ANY_URL = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
const HEIC_TYPES = ['image/heic', 'image/heif'];

let loadPromise: Promise<void> | null = null;

function loadHeic2Any(): Promise<void> {
  if (window.heic2any) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = HEIC2ANY_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load heic2any'));
    document.head.appendChild(script);
  });
  return loadPromise;
}

function isHeicFile(file: File): boolean {
  if (HEIC_TYPES.includes(file.type.toLowerCase())) return true;
  // Safari sometimes reports an empty MIME type for HEIC files picked via
  // the native photo picker — fall back to the filename extension.
  if (file.type === '' && /\.hei[cf]$/i.test(file.name)) return true;
  return false;
}

/** Returns the original file unchanged unless it's HEIC/HEIF, in which case
 *  it's converted to a JPEG File with the same base name. */
export async function normalizeImageFile(file: File): Promise<File> {
  if (!isHeicFile(file)) return file;

  await loadHeic2Any();
  if (!window.heic2any) throw new Error('heic2any unavailable');

  const result = await window.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  const blob = Array.isArray(result) ? result[0] : result;
  const newName = file.name.replace(/\.hei[cf]$/i, '.jpg') || 'photo.jpg';
  return new File([blob], newName, { type: 'image/jpeg' });
}
